import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DONE_STEP_ID,
  fetchPreEmploymentEducationProgress,
  upsertPreEmploymentEducationProgress,
} from './preEmploymentEducationApi';
import type {
  PreEmploymentEducationFormValues,
  PreEmploymentEducationStep,
  YesNo,
} from './preEmploymentEducationTypes';

export type PreEmploymentEducationCommittedEntry = {
  stepIndex: number;
  stepId: PreEmploymentEducationStep;
  fieldName: keyof PreEmploymentEducationFormValues;
  displayText: string;
  rawValue: string;
};

type StepDef = {
  stepId: PreEmploymentEducationStep;
  fieldName: keyof PreEmploymentEducationFormValues;
  kind: 'choice' | 'agree';
};

const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

function isYes(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  return v === 'yes' || v === 'true';
}

function formatYesNo(t: (k: string) => string, raw: string): string {
  return isYes(raw) ? t('submitDocuments.preEmploymentEducation.yes') : t('submitDocuments.preEmploymentEducation.no');
}

function formatAgree(t: (k: string) => string, raw: string): string {
  return isYes(raw) ? t('submitDocuments.preEmploymentEducation.agreeCheckbox') : '';
}

function buildSteps(values: PreEmploymentEducationFormValues): StepDef[] {
  const edu = values.educationUnderstood?.trim().toLowerCase();
  if (edu === 'no') {
    return [{ stepId: 'materialsUnderstood', fieldName: 'educationUnderstood', kind: 'choice' }];
  }
  return [
    { stepId: 'materialsUnderstood', fieldName: 'educationUnderstood', kind: 'choice' },
    { stepId: 'agree', fieldName: 'agree', kind: 'agree' },
  ];
}

function cursorStepIdForSave(steps: StepDef[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) return DONE_STEP_ID;
  return steps[stepIndex]?.stepId ?? null;
}

function upsertCommittedEntry(
  committed: PreEmploymentEducationCommittedEntry[],
  entry: PreEmploymentEducationCommittedEntry
): PreEmploymentEducationCommittedEntry[] {
  const next = [...committed];
  const idx = next.findIndex((c) => c.stepId === entry.stepId);
  if (idx >= 0) {
    next[idx] = entry;
    return next;
  }
  next.push(entry);
  return next;
}

function rebuildFromProgress(
  values: PreEmploymentEducationFormValues,
  currentStepId: string | null | undefined,
  t: (k: string) => string
): {
  stepIndex: number;
  committed: PreEmploymentEducationCommittedEntry[];
  isComplete: boolean;
  materialsDeclined: boolean;
} {
  const steps = buildSteps(values);
  const edu = values.educationUnderstood?.trim().toLowerCase();

  if (edu === 'no') {
    const entry: PreEmploymentEducationCommittedEntry = {
      stepIndex: 0,
      stepId: 'materialsUnderstood',
      fieldName: 'educationUnderstood',
      rawValue: 'no',
      displayText: formatYesNo(t, 'no'),
    };
    return { stepIndex: 1, committed: [entry], isComplete: false, materialsDeclined: true };
  }

  if (currentStepId === DONE_STEP_ID) {
    const committed: PreEmploymentEducationCommittedEntry[] = steps.map((s, i) => {
      const raw = String(values[s.fieldName] ?? '').trim();
      const displayText =
        s.stepId === 'materialsUnderstood' ? formatYesNo(t, raw) : formatAgree(t, raw);
      return {
        stepIndex: i,
        stepId: s.stepId,
        fieldName: s.fieldName,
        rawValue: raw,
        displayText,
      };
    });
    return { stepIndex: steps.length, committed, isComplete: true, materialsDeclined: false };
  }

  const idx = currentStepId ? steps.findIndex((s) => s.stepId === currentStepId) : -1;
  const stepIndex = idx >= 0 ? idx : 0;
  const committed: PreEmploymentEducationCommittedEntry[] = [];
  for (let i = 0; i < stepIndex; i++) {
    const s = steps[i];
    const raw = String(values[s.fieldName] ?? '').trim();
    committed.push({
      stepIndex: i,
      stepId: s.stepId,
      fieldName: s.fieldName,
      rawValue: raw,
      displayText:
        s.stepId === 'materialsUnderstood' ? formatYesNo(t, raw) : formatAgree(t, raw),
    });
  }
  return { stepIndex, committed, isComplete: false, materialsDeclined: false };
}

export function usePreEmploymentEducationFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: (k: string) => string
) {
  const [values, setValues] = useState<PreEmploymentEducationFormValues>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [committed, setCommitted] = useState<PreEmploymentEducationCommittedEntry[]>([]);
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [materialsDeclined, setMaterialsDeclined] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const progressLoadedRef = useRef(false);

  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const assistantHoldTimerRef = useRef<number | null>(null);
  const [suppressNextAssistantBubble, setSuppressNextAssistantBubble] = useState(false);

  const steps = useMemo(() => buildSteps(values), [values]);

  const currentStep: StepDef | undefined = useMemo(() => {
    if (!steps.length) return undefined;
    if (editingCommitIndex !== null) return steps[editingCommitIndex];
    if (stepIndex < steps.length) return steps[stepIndex];
    return undefined;
  }, [steps, stepIndex, editingCommitIndex]);

  const isComplete =
    !materialsDeclined && steps.length > 0 && stepIndex >= steps.length && editingCommitIndex === null;

  const showSafetyRulesBlock = useMemo(() => {
    if (materialsDeclined) return false;
    const materialsEntry = committed.find((c) => c.stepId === 'materialsUnderstood');
    if (materialsEntry && isYes(materialsEntry.rawValue)) return true;
    if (currentStep?.stepId === 'agree') return true;
    return committed.some((c) => c.stepId === 'agree');
  }, [committed, currentStep, materialsDeclined]);

  const assistantQuestionVisible = !suppressNextAssistantBubble;

  const clearAssistantHold = useCallback(() => {
    if (assistantHoldTimerRef.current != null) {
      window.clearTimeout(assistantHoldTimerRef.current);
      assistantHoldTimerRef.current = null;
    }
    setSuppressNextAssistantBubble(false);
  }, []);

  useEffect(() => () => clearAssistantHold(), [clearAssistantHold]);

  const persistSnapshot = useCallback(
    async (
      nextValues: PreEmploymentEducationFormValues,
      nextStepIndex: number,
      stepsSnapshot: StepDef[]
    ): Promise<boolean> => {
      const email = currentEmail?.trim();
      const invId = invitationId?.trim();
      if (!email || !invId) return true;
      const ok = await upsertPreEmploymentEducationProgress({
        email,
        invitationId: invId,
        values: nextValues,
        currentStepId: cursorStepIdForSave(stepsSnapshot, nextStepIndex),
      });
      if (!ok) {
        setSaveError(t('submitDocuments.preEmploymentEducation.saveFailed'));
      } else {
        setSaveError(null);
        setValues((prev) => ({ ...prev, ...ok.values }));
      }
      return ok != null;
    },
    [currentEmail, invitationId, t]
  );

  const handleMaterialsDeclined = useCallback(
    async (entry: PreEmploymentEducationCommittedEntry): Promise<boolean> => {
      const email = currentEmail?.trim();
      const invId = invitationId?.trim();
      if (!email || !invId) return true;
      const ok = await upsertPreEmploymentEducationProgress({
        email,
        invitationId: invId,
        values: { educationUnderstood: 'no', agree: 'false' },
        currentStepId: null,
      });
      if (!ok) {
        setSaveError(t('submitDocuments.preEmploymentEducation.saveFailed'));
        return false;
      }
      setSaveError(null);
      clearAssistantHold();
      setMaterialsDeclined(true);
      setValues({ educationUnderstood: 'no' });
      setCommitted([entry]);
      setStepIndex(1);
      setEditingCommitIndex(null);
      setAgreeChecked(false);
      return true;
    },
    [currentEmail, invitationId, t, clearAssistantHold]
  );

  useEffect(() => {
    if (!currentEmail?.trim() || !invitationId?.trim() || progressLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchPreEmploymentEducationProgress(currentEmail, invitationId);
        if (cancelled || !data) return;
        progressLoadedRef.current = true;
        const loadedValues = data.values ?? {};
        setValues(loadedValues);
        const rebuilt = rebuildFromProgress(loadedValues, data.currentStepId, t);
        setCommitted(rebuilt.committed);
        setStepIndex(rebuilt.stepIndex);
        setMaterialsDeclined(rebuilt.materialsDeclined);
        setAgreeChecked(loadedValues.agree === 'true');
      } catch {
        progressLoadedRef.current = true;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEmail, invitationId, t]);

  const advanceMaterials = useCallback(
    async (choice: YesNo) => {
      if (!currentStep || currentStep.stepId !== 'materialsUnderstood') return;
      const entry: PreEmploymentEducationCommittedEntry = {
        stepIndex: editingCommitIndex ?? stepIndex,
        stepId: 'materialsUnderstood',
        fieldName: 'educationUnderstood',
        rawValue: choice,
        displayText: formatYesNo(t, choice),
      };
      const nextValues: PreEmploymentEducationFormValues = {
        ...values,
        educationUnderstood: choice,
      };
      if (choice === 'no') {
        if (editingCommitIndex !== null) {
          setEditingCommitIndex(null);
        }
        await handleMaterialsDeclined(entry);
        return;
      }

      setValues(nextValues);
      setMaterialsDeclined(false);

      if (editingCommitIndex !== null) {
        const hadAgree =
          committed.some((c) => c.stepId === 'agree' && isYes(c.rawValue)) ||
          values.agree === 'true';

        if (hadAgree) {
          setCommitted((prev) => upsertCommittedEntry(prev, entry));
          const finalSteps = buildSteps(nextValues);
          setStepIndex(finalSteps.length);
          setEditingCommitIndex(null);
          await persistSnapshot(nextValues, finalSteps.length, finalSteps);
          return;
        }

        setCommitted((prev) => upsertCommittedEntry(prev, entry));
        setAgreeChecked(false);
        delete nextValues.agree;
        const nextSteps = buildSteps(nextValues);
        const agreeIdx = nextSteps.findIndex((s) => s.stepId === 'agree');
        setStepIndex(agreeIdx >= 0 ? agreeIdx : nextSteps.length);
        setEditingCommitIndex(null);
        await persistSnapshot(nextValues, agreeIdx >= 0 ? agreeIdx : nextSteps.length, nextSteps);
        return;
      }

      clearAssistantHold();
      setSuppressNextAssistantBubble(true);
      assistantHoldTimerRef.current = window.setTimeout(() => {
        assistantHoldTimerRef.current = null;
        setSuppressNextAssistantBubble(false);
      }, NEXT_ASSISTANT_QUESTION_DELAY_MS);

      setCommitted((prev) => [...prev, entry]);
      setStepIndex((i) => i + 1);
      const nextSteps = buildSteps(nextValues);
      await persistSnapshot(nextValues, stepIndex + 1, nextSteps);
    },
    [
      currentStep,
      editingCommitIndex,
      stepIndex,
      values,
      t,
      handleMaterialsDeclined,
      persistSnapshot,
      clearAssistantHold,
    ]
  );

  const advanceAgree = useCallback(async () => {
    if (!agreeChecked) return;
    const entry: PreEmploymentEducationCommittedEntry = {
      stepIndex: editingCommitIndex ?? stepIndex,
      stepId: 'agree',
      fieldName: 'agree',
      rawValue: 'true',
      displayText: t('submitDocuments.preEmploymentEducation.agreeCheckbox'),
    };
    const nextValues: PreEmploymentEducationFormValues = { ...values, agree: 'true' };
    setValues(nextValues);

    if (editingCommitIndex !== null) {
      setCommitted((prev) => upsertCommittedEntry(prev, entry));
      const finalSteps = buildSteps(nextValues);
      setStepIndex(finalSteps.length);
      setEditingCommitIndex(null);
      await persistSnapshot(nextValues, finalSteps.length, finalSteps);
      return;
    }

    clearAssistantHold();
    setCommitted((prev) => [...prev, entry]);
    const finalSteps = buildSteps(nextValues);
    setStepIndex(finalSteps.length);
    await persistSnapshot(nextValues, finalSteps.length, finalSteps);
  }, [agreeChecked, editingCommitIndex, stepIndex, values, t, persistSnapshot, clearAssistantHold]);

  const undo = useCallback(() => {
    clearAssistantHold();
    setCommitted((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const nextStepIndex = prev.length - 1;
      setValues((v) => {
        const next = { ...v };
        delete next[last.fieldName];
        if (last.stepId === 'materialsUnderstood') {
          delete next.agree;
          delete next.consentDateFf014;
          setMaterialsDeclined(false);
          setAgreeChecked(false);
        }
        if (last.stepId === 'agree') {
          next.agree = 'false';
          delete next.consentDateFf014;
          setAgreeChecked(false);
        }
        void persistSnapshot(next, nextStepIndex, buildSteps(next));
        return next;
      });
      setStepIndex(nextStepIndex);
      setEditingCommitIndex(null);
      return prev.slice(0, -1);
    });
  }, [clearAssistantHold, persistSnapshot]);

  const showEditForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (materialsDeclined) return false;
      if (!isComplete || committed.length === 0) return false;
      return commitIndex >= 0 && commitIndex < committed.length;
    },
    [editingCommitIndex, materialsDeclined, isComplete, committed.length]
  );

  const jumpToEditCommitIndex = useCallback(
    (commitIndex: number) => {
      if (!isComplete || commitIndex < 0 || commitIndex >= committed.length) return;
      const entry = committed[commitIndex];
      const stepIdx = steps.findIndex((s) => s.stepId === entry.stepId);
      if (stepIdx < 0) return;
      clearAssistantHold();
      if (entry.stepId === 'agree') {
        setAgreeChecked(entry.rawValue === 'true');
      }
      setEditingCommitIndex(stepIdx);
    },
    [isComplete, committed, steps, clearAssistantHold]
  );

  const showUndoForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (materialsDeclined) return commitIndex === 0;
      if (isComplete && committed.length > 0) return commitIndex === committed.length - 1;
      return stepIndex === commitIndex + 1;
    },
    [editingCommitIndex, materialsDeclined, isComplete, committed.length, stepIndex]
  );

  const handleFinalComplete = useCallback(async (): Promise<boolean> => {
    const email = currentEmail?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) return true;
    const ok = await upsertPreEmploymentEducationProgress({
      email,
      invitationId: invId,
      values: { ...values, educationUnderstood: 'yes', agree: 'true' },
      currentStepId: DONE_STEP_ID,
    });
    if (!ok) {
      setSaveError(t('submitDocuments.preEmploymentEducation.saveFailed'));
      return false;
    }
    setValues((prev) => ({ ...prev, ...ok.values }));
    return true;
  }, [currentEmail, invitationId, values, t]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [committed, stepIndex, isComplete, materialsDeclined, suppressNextAssistantBubble]);

  return {
    t,
    values,
    committed,
    currentStep,
    stepIndex,
    isLoading,
    isComplete,
    materialsDeclined,
    agreeChecked,
    setAgreeChecked,
    saveError,
    threadEndRef,
    assistantQuestionVisible,
    showSafetyRulesBlock,
    advanceMaterials,
    advanceAgree,
    undo,
    showEditForCommitIndex,
    jumpToEditCommitIndex,
    showUndoForCommitIndex,
    editingCommitIndex,
    handleFinalComplete,
  };
}
